import React from 'react';
import { MapPin, Phone, FileText, CheckCircle, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Reveal, RevealGroup, RevealItem } from '../components/motion/Reveal';
import Seo from '../components/Seo';

const FeeScale = () => {
  const lagosLGAs = [
    // Island Zone
    { name: "Eti-Osa", zone: "Island", areas: "Ikoyi, Victoria Island, Lekki, Ajah" },
    { name: "Lagos Island", zone: "Island", areas: "Lagos Island, Obalende, Ebute Metta" },
    
    // Mainland - Central
    { name: "Ikeja", zone: "Mainland", areas: "Ikeja GRA, Allen Avenue, Alausa" },
    { name: "Surulere", zone: "Mainland", areas: "Surulere, Adeniran Ogunsanya" },
    { name: "Lagos Mainland", zone: "Mainland", areas: "Yaba, Ebute Metta, Oyingbo" },
    { name: "Shomolu", zone: "Mainland", areas: "Shomolu, Bariga, Palmgrove" },
    { name: "Kosofe", zone: "Mainland", areas: "Ketu, Anthony, Ojota, Ikosi" },
    { name: "Mushin", zone: "Mainland", areas: "Mushin, Idi-Araba, Papa Ajao" },
    
    // Mainland - East
    { name: "Amuwo-Odofin", zone: "Mainland", areas: "Festac, Satellite Town, Mile 2" },
    { name: "Apapa", zone: "Mainland", areas: "Apapa, Ajegunle, Kirikiri" },
    { name: "Ojo", zone: "Mainland", areas: "Ojo, Alaba, Okokomaiko" },
    { name: "Ajeromi-Ifelodun", zone: "Mainland", areas: "Ajegunle, Olodi, Layeni" },
    { name: "Oshodi-Isolo", zone: "Mainland", areas: "Oshodi, Isolo, Ejigbo, Okota" },
    
    // Mainland - West & North
    { name: "Alimosho", zone: "Mainland", areas: "Ikotun, Igando, Egbeda, Ipaja" },
    { name: "Agege", zone: "Mainland", areas: "Agege, Orile Agege, Mangoro" },
    { name: "Ifako-Ijaiye", zone: "Mainland", areas: "Ifako, Ijaiye, Abule Egba" },
    
    // Emerging/Outskirts
    { name: "Ibeju-Lekki", zone: "Emerging", areas: "Ibeju, Akodo, Free Trade Zone" },
    { name: "Epe", zone: "Emerging", areas: "Epe, Ejinrin, Itokin" },
    { name: "Ikorodu", zone: "Emerging", areas: "Ikorodu, Igbogbo, Imota" },
    { name: "Badagry", zone: "Emerging", areas: "Badagry, Ajara, Iworo" }
  ];

  const services = [
    { name: "Cadastral Survey", description: "Property boundary survey" },
    { name: "Topographic Survey", description: "Land features and contours" },
    { name: "Survey Plan Preparation", description: "Official plan preparation" },
    { name: "Survey Search", description: "Document verification" },
    { name: "Boundary Re-establishment", description: "Lost beacon restoration" },
    { name: "Land Status Check", description: "Government acquisition verification" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Survey Fee Scale — All 20 Lagos LGAs | Versatile Geopick"
        description="Surveying services across all 20 Lagos Local Government Areas. Request a customized quote for your property location via WhatsApp."
      />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-stone-100 via-amber-50/30 to-stone-50 pt-24">
        <Reveal className="text-center px-6" duration={0.9}>
          <Phone className="h-16 w-16 text-emerald-700 mx-auto mb-6" />
          <h1 className="text-5xl md:text-7xl font-light text-stone-800 tracking-tight">
            Service <span className="font-semibold text-emerald-700">Pricing</span>
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto mt-6">
            Get accurate quotations tailored to your property needs
          </p>
        </Reveal>
      </section>

      {/* Important Notice */}
      <section className="py-16 bg-emerald-50 border-y border-emerald-200">
        <Reveal className="max-w-4xl mx-auto px-6 text-center" direction="scale">
          <h3 className="text-2xl font-medium text-emerald-800 mb-4">Customized Pricing for Your Project</h3>
          <p className="text-lg text-stone-700 leading-relaxed">
            Survey fees vary based on land size, location, complexity, accessibility, and specific requirements. 
            We provide <strong className="text-emerald-700">accurate quotations</strong> tailored to your unique property needs.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="tel:+2348034494507"
              className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-700 text-white rounded-2xl font-medium hover:bg-emerald-800 transition-all duration-300 hover:shadow-lg"
            >
              <Phone className="h-5 w-5" />
              Call for Quote: +234 803 449 4507
            </a>
            <a
              href="https://wa.me/2348034494507"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-emerald-700 border-2 border-emerald-700 rounded-2xl font-medium hover:bg-emerald-50 transition-all duration-300"
            >
              WhatsApp for Pricing
            </a>
          </div>
        </Reveal>
      </section>

      {/* Fee Structure by LGA */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-emerald-700 font-medium tracking-wider uppercase text-sm mb-3">Complete Lagos State Coverage</p>
            <h2 className="text-4xl md:text-5xl font-light text-stone-800 tracking-tight mb-4">
              All 20 Local Government Areas
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto mb-8">
              Professional surveying services across every Local Government Area in Lagos State
            </p>
            <div className="inline-block bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4">
              <p className="text-stone-700">
                <strong className="text-amber-700">Get Your Custom Quote:</strong> Contact us with your property location and requirements for accurate pricing
              </p>
            </div>
          </div>

          <RevealGroup className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" stagger={0.06}>
            {lagosLGAs.map((lga, index) => {
              // Create WhatsApp message with LGA details
              const whatsappMessage = `Hello, I'm requesting a quote for survey services in *${lga.name} LGA* (${lga.areas}). Please provide pricing information.`;
              const whatsappLink = `https://wa.me/2348034494507?text=${encodeURIComponent(whatsappMessage)}`;
              
              return (
                <RevealItem key={index}>
                <Card className="border h-full border-stone-200 hover:border-emerald-300 transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-stone-800 mb-2">{lga.name}</h3>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                          lga.zone === 'Island' ? 'bg-emerald-100 text-emerald-700' :
                          lga.zone === 'Emerging' ? 'bg-amber-100 text-amber-700' :
                          'bg-stone-100 text-stone-700'
                        }`}>
                          {lga.zone}
                        </span>
                      </div>
                      <MapPin className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                    </div>
                    <p className="text-sm text-stone-600 mb-4 leading-relaxed">
                      {lga.areas}
                    </p>
                    <div className="pt-4 border-t border-stone-200">
                      <a 
                        href={whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-700 hover:text-emerald-800 font-medium text-sm transition-colors duration-300 inline-flex items-center gap-1"
                      >
                        Request Quote <Phone className="h-4 w-4" />
                      </a>
                    </div>
                  </CardContent>
                </Card>
                </RevealItem>
              );
            })}
          </RevealGroup>

          <div className="mt-16 text-center">
            <Card className="border-2 border-emerald-200 bg-emerald-50 max-w-3xl mx-auto">
              <CardContent className="p-8">
                <h3 className="text-2xl font-medium text-stone-800 mb-4">
                  Why Contact Us for Pricing?
                </h3>
                <div className="grid md:grid-cols-2 gap-4 text-left">
                  {[
                    "Accurate quotes based on property size",
                    "Special rates for multiple plots",
                    "Transparent breakdown of costs",
                    "No hidden fees or surprise charges"
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                      <span className="text-stone-700">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 bg-gradient-to-br from-stone-50 to-amber-50/20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-stone-800 tracking-tight mb-4">
              Our Survey Services
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Comprehensive solutions for all your land surveying needs
            </p>
          </div>

          <RevealGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              // Create WhatsApp message with service details
              const whatsappMessage = `Hello, I'm requesting pricing for *${service.name}* - ${service.description}. Please provide a quotation.`;
              const whatsappLink = `https://wa.me/2348034494507?text=${encodeURIComponent(whatsappMessage)}`;
              
              return (
                <RevealItem key={index}>
                <Card className="border h-full border-stone-200 hover:border-emerald-200 transition-all duration-300 hover:-translate-y-1">
                  <CardHeader>
                    <CardTitle className="text-xl text-stone-800">{service.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-stone-600 mb-6">{service.description}</p>
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800 font-medium transition-colors duration-300"
                    >
                      Get Pricing <Phone className="h-4 w-4" />
                    </a>
                  </CardContent>
                </Card>
                </RevealItem>
              );
            })}
          </RevealGroup>

          <div className="mt-16 text-center">
            <p className="text-lg text-stone-600 mb-6">
              Need pricing for a specific service?
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://wa.me/2348034494507"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-10 py-4 bg-green-600 text-white rounded-2xl font-medium hover:bg-green-700 transition-all duration-300"
              >
                WhatsApp for Quote
              </a>
              <a
                href="mailto:Versatilegeopick@gmail.com"
                className="inline-block px-10 py-4 bg-white border-2 border-emerald-700 text-emerald-700 rounded-2xl font-medium hover:bg-emerald-50 transition-all duration-300"
              >
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-light text-stone-800 text-center mb-12">What's Included in Survey Fees</h2>
          <RevealGroup className="grid md:grid-cols-2 gap-6" stagger={0.08}>
            {[
              "Site inspection and measurement",
              "Computation and plan preparation",
              "Government authentication processing",
              "Beacon installation at corners",
              "Coordinate determination (GPS/Total Station)",
              "Professional surveyor certification",
              "Survey report documentation",
              "Follow-up consultations"
            ].map((item, index) => (
              <RevealItem key={index} direction="left" className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-emerald-700 flex-shrink-0 mt-0.5" />
                <p className="text-stone-700">{item}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-emerald-700">
        <Reveal className="max-w-4xl mx-auto px-6 text-center" direction="scale">
          <h2 className="text-4xl font-light text-white mb-6">
            Need an Accurate Quotation?
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Contact us with your property details for a precise fee estimate
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+2348034494507"
              className="inline-block px-10 py-4 bg-white text-emerald-700 rounded-2xl font-medium hover:bg-stone-50 transition-all duration-300"
            >
              Call: +234 803 449 4507
            </a>
            <a
              href="https://wa.me/2348034494507"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-4 bg-emerald-600 text-white rounded-2xl font-medium hover:bg-emerald-500 transition-all duration-300"
            >
              WhatsApp Us
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default FeeScale;
